import { useCallback, useEffect, useMemo } from 'react';
import cx from 'classnames';
import { motion } from 'framer-motion';

import { Toast } from 'components';

import { ICONS, Notification, useNotification } from './Notifications.store';

import styles from './Notifications.module.scss';

export type AnimatedToastProps = {
  notification: Notification;
  onClick?(notification: Notification): void;
};

const useAutoDismiss = (notification: Notification) => {
  const { close } = useNotification();

  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;

    if (notification.options?.autoDismiss && notification.options?.duration !== Infinity) {
      timeout = setTimeout(() => {
        close(notification.id);
        timeout = null;
      }, notification.options?.duration);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
    };
  }, [notification.id, notification.options?.autoDismiss, notification.options?.duration]);
};

const AnimatedNotification = ({ notification, onClick }: AnimatedToastProps) => {
  useAutoDismiss(notification);

  return (
    <motion.div
      layout="position"
      style={useMemo(() => ({ pointerEvents: 'none' }), [])}
      transition={useMemo(() => ({ type: 'spring', stiffness: 300, damping: 30 }), [])}
      initial={useMemo(() => ({ y: -100, opacity: 0 }), [])}
      animate={useMemo(() => ({ y: 0, opacity: 1 }), [])}
      exit={useMemo(() => ({ opacity: 0 }), [])}
    >
      <Toast
        className={cx(styles.toast, { [styles.dismissable]: notification.options?.dismissOnClick })}
        icon={ICONS[notification.type]}
        onClick={useCallback(() => onClick?.(notification), [notification, onClick])}
      >
        {notification.content}
      </Toast>
    </motion.div>
  );
};

export default AnimatedNotification;
